/**
 * Version Store — Xyden Leadership Assessment Platform
 * Step 6F Green Phase (Zustand)
 *
 * Note: Tests mock this store via vi.mock('@/stores/versionStore').
 * This implementation is used in production mode.
 *
 * Persistence: ``selectedVersion`` is persisted to localStorage so the
 * user's chosen version survives a page reload. The VersionSelector
 * component validates the persisted id against the current API
 * response on mount and auto-falls-back if the persisted version was
 * deleted or archived.
 *
 * ``refreshKey`` and ``activeVersion`` are intentionally NOT persisted
 * — both are session-scoped (refreshKey is a re-render bumper;
 * activeVersion is whichever version is currently in the Active state
 * server-side, which is fetched fresh).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Version } from '../types';

const initialState = {
  activeVersion: null as Version | null,
  selectedVersion: null as Version | null,
  refreshKey: 0,
};

interface VersionStoreState {
  activeVersion: Version | null;
  selectedVersion: Version | null;
  refreshKey: number;
  setActiveVersion: (version: Version | null) => void;
  setSelectedVersion: (version: Version | null) => void;
  triggerRefresh: () => void;
  reset: () => void;
}

export const useVersionStore = create<VersionStoreState>()(
  persist(
    (set) => ({
      ...initialState,

      setActiveVersion: (version: Version | null) => {
        set({ activeVersion: version });
      },

      setSelectedVersion: (version: Version | null) => {
        set({ selectedVersion: version });
      },

      triggerRefresh: () => {
        set((s) => ({ refreshKey: s.refreshKey + 1 }));
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'xyden-version-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist the user's selection. activeVersion + refreshKey
      // are session-scoped and reset cleanly on each page load.
      partialize: (state) => ({ selectedVersion: state.selectedVersion }),
    },
  ),
);
